import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Check, X, Sparkles } from 'lucide-react';

const withUgcmaxFeatures = [
  '動画制作に必要な<strong>すべての機能</strong>が含まれています',
  'AIによる<strong>自動編集と最適化</strong>',
  '<strong>無制限</strong>の動画生成',
  '<strong>24時間365日</strong>いつでも利用可能',
  '<strong>高品質な</strong>ショート動画制作',
  '<strong>カスタマイズ可能な</strong>テンプレート'
];

const traditionalFeatures = [
  '編集者の<strong>スケジュール調整</strong>が必要',
  '修正依頼に<strong>時間がかかる</strong>',
  '<strong>高額な</strong>制作費用',
  '<strong>限られた</strong>修正回数',
  '納品まで<strong>時間がかかる</strong>',
  '<strong>追加料金</strong>が発生することも'
];

export default function ServiceComparison() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto" ref={ref}>
          <div className="text-center mb-20">
            
          <p className={`text-xl text-gray-600 transition-all duration-700 delay-200 mt-12 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              UGC動画制作のコストを
            </p>
            <p className={`text-5xl md:text-7xl font-extrabold transition-all duration-700 delay-300 mt-4 mb-2 bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent drop-shadow-sm ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              96%削減
            </p>
            <p className={`text-base text-gray-500 transition-all duration-700 delay-400 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              ＊従来の動画制作会社と比較
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className={`relative bg-gradient-to-br from-indigo-50 via-blue-50 to-white p-8 rounded-3xl border-2 border-indigo-500 transition-all duration-700 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            } hover:shadow-2xl hover:-translate-y-1 transform transition-all`}>
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                おすすめ
              </div>
              <div className="flex items-center gap-3 mb-8">
                <Sparkles className="w-6 h-6 text-indigo-500" />
                <h3 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent">Shortmakerの場合</h3>
              </div>

              <ul className="space-y-4 mb-8">
                {withUgcmaxFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 group">
                    <div className="bg-indigo-100 p-1.5 rounded-full group-hover:bg-indigo-200 transition-colors">
                      <Check className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: feature }}></span>
                  </li>
                ))}
              </ul>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-6xl font-bold bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent">480円</span>
                  <span className="text-gray-600">/動画</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">追加料金なし</p>
              </div>

              <button className="w-full bg-gradient-to-r from-indigo-600 to-blue-500 text-white px-8 py-4 rounded-xl hover:from-indigo-700 hover:to-blue-600 transition-all duration-300 text-lg font-medium shadow-lg hover:shadow-xl">
                無料で始める
              </button>
            </div>

            <div className={`bg-white p-6 rounded-2xl border border-gray-100 transition-all duration-700 ${
              inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            } hover:shadow-md scale-95`}>
              <h3 className="text-lg font-medium text-gray-500 mb-6 flex items-center gap-2">
                <X className="w-4 h-4" />
                従来のショート動画製作
              </h3>

              <ul className="space-y-3 mb-6">
                {traditionalFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3 group">
                    <div className="bg-gray-100 p-1.5 rounded-full group-hover:bg-gray-200 transition-colors">
                      <X className="w-3 h-3 text-gray-500" />
                    </div>
                    <span className="text-gray-500 text-sm" dangerouslySetInnerHTML={{ __html: feature }}></span>
                  </li>
                ))}
              </ul>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-gray-500">16,000円〜</span>
                  <span className="text-gray-400">/動画</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">+ 追加料金</p>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <div className="inline-flex flex-col md:flex-row items-center gap-4 md:gap-8 px-8 py-6 bg-gradient-to-br from-primary-50 via-accent-50 to-white rounded-2xl border border-primary-100 shadow-lg mb-12">
              <div className="flex items-center gap-3 text-xl md:text-2xl font-bold">
                <div className="bg-primary-100 p-2 rounded-xl">
                  <span className="bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">100倍</span>
                </div>
                <span className="text-gray-600">のコスト効率</span>
              </div>
              <div className="hidden md:block w-px h-12 bg-primary-100"></div>
              <div className="flex items-center gap-3 text-xl md:text-2xl font-bold">
                <div className="bg-accent-100 p-2 rounded-xl">
                  <span className="bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent">10倍</span>
                </div>
                <span className="text-gray-600">のスピード</span>
              </div>
              <div className="hidden md:block w-px h-12 bg-primary-100"></div>
              <div className="flex items-center gap-3 text-xl md:text-2xl font-bold">
                <span className="text-gray-600">で</span>
                <div className="bg-gradient-to-r from-primary-600 to-accent-500 text-white px-4 py-2 rounded-xl">
                  マーケティングを成功
                </div>
              </div>
            </div>
            <button className="bg-gradient-to-r from-primary-600 to-accent-500 text-white px-16 py-6 rounded-full hover:from-primary-700 hover:to-accent-600 transition-all duration-300 text-xl font-extrabold tracking-wide shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              最初のAI動画を作成
            </button>
          </div>
        </div>
      </div>
    </section>
  );
} 